import type { ReactNode } from 'react';

import type {
  ChargeLineItem,
  ChargeLinePreviewResponse,
  EncounterLedgerGroup,
  PatientResponsibilityBreakdown,
} from '../../store';

export type ChargeDisplayStatus =
  | 'open'
  | 'invoiced'
  | 'paid'
  | 'written_off'
  | 'mixed';

export interface DisplayChargeLine {
  id: string;
  code?: string | null;
  description?: string | null;
  units?: number | null;
  amountCents: number;
  chargedCents?: number | null;
  allowedCents?: number | null;
  insurancePaidCents?: number | null;
  patientPaidCents?: number | null;
  appliedPaymentCents?: number | null;
  status: ChargeDisplayStatus;
  responsibilityLabel?: string | null;
}

export interface DisplayEncounterChargeGroup {
  id: string | null;
  serviceDate?: string | null;
  encounterTypeLabel?: string | null;
  providerName?: string | null;
  payerName?: string | null;
  totalCents: number;
  status: ChargeDisplayStatus;
  lines: DisplayChargeLine[];
}

export type EncounterChargeGroupMode = 'readonly' | 'selectable' | 'actionable';

export interface EncounterChargeGroupProps {
  group: DisplayEncounterChargeGroup;
  mode?: EncounterChargeGroupMode;
  selected?: boolean;
  disabled?: boolean;
  compact?: boolean;
  showLineMath?: boolean;
  selectableLineIds?: Set<string>;
  selectedLineIds?: Set<string>;
  actions?: ReactNode;
  onSelectedChange?: (selected: boolean) => void;
  onLineSelectedChange?: (lineId: string, selected: boolean) => void;
}

const STATUS_MAP: Record<string, ChargeDisplayStatus> = {
  confirmed: 'open',
  operational_expected: 'open',
  estimated: 'open',
  invoiced: 'invoiced',
  in_collections: 'invoiced',
  paid: 'paid',
  written_off: 'written_off',
};

export function chargeStatusFromRaw(status: string | null | undefined): ChargeDisplayStatus {
  if (!status) return 'open';
  return STATUS_MAP[status] ?? 'open';
}

export function deriveGroupStatus(lines: DisplayChargeLine[]): ChargeDisplayStatus {
  if (lines.length === 0) return 'open';
  const statuses = new Set(lines.map((line) => line.status));
  if (statuses.size === 1) return lines[0].status;
  return 'mixed';
}

export function displayGroupFromEncounterLedgerGroup(
  encounter: EncounterLedgerGroup,
): DisplayEncounterChargeGroup {
  const lines = (encounter.charge_lines ?? []).map(displayLineFromChargeLineItem);
  return {
    id: encounter.encounter_id ?? null,
    serviceDate: encounter.service_date,
    encounterTypeLabel: encounter.encounter_type_label,
    providerName: encounter.provider_name,
    payerName: encounter.primary_payer_name,
    totalCents: encounter.patient_balance_cents ?? encounter.patient_due_cents ?? 0,
    status: deriveGroupStatus(lines),
    lines,
  };
}

export function displayGroupsFromPreview(
  preview: ChargeLinePreviewResponse | undefined,
): DisplayEncounterChargeGroup[] {
  if (!preview) return [];
  const responsibilityLabels = responsibilityLabelsForPreview(preview);
  return (preview.encounters ?? []).map((encounter) => {
    const lines: DisplayChargeLine[] = (encounter.lines ?? []).map((line) => {
      const status = line.already_invoiced
        ? 'invoiced'
        : line.patient_due_cents === 0
          ? 'paid'
          : 'open';
      return {
        id: line.charge_line_id,
        code: line.cpt_code,
        description: line.description,
        amountCents: line.patient_due_cents,
        appliedPaymentCents: line.applied_payment_cents ?? 0,
        status,
        responsibilityLabel: responsibilityLabels.get(line.charge_line_id),
      };
    });
    return {
      id: encounter.encounter_id ?? null,
      serviceDate: encounter.service_date,
      encounterTypeLabel: encounter.encounter_type_label,
      providerName: encounter.provider_name,
      totalCents: encounter.encounter_due_cents,
      status: deriveGroupStatus(lines),
      lines,
    };
  });
}

function displayLineFromChargeLineItem(line: ChargeLineItem): DisplayChargeLine {
  return {
    id: line.charge_line_id,
    code: line.cpt_code,
    description: line.description,
    units: line.units,
    amountCents: line.patient_responsibility_cents ?? line.patient_paid_cents ?? 0,
    chargedCents: line.charged_cents,
    allowedCents: line.allowed_cents,
    insurancePaidCents: line.insurance_paid_cents,
    patientPaidCents: line.patient_paid_cents,
    status: chargeStatusFromRaw(line.status),
  };
}

function responsibilityLabelsForPreview(
  preview: ChargeLinePreviewResponse,
): Map<string, string> {
  const labelByLineId = new Map<string, string>();
  const breakdownItems = responsibilityBreakdownItems(
    preview.patient_responsibility_breakdown,
  );
  if (breakdownItems.length === 0) return labelByLineId;

  const lines = (preview.encounters ?? []).flatMap((encounter) => encounter.lines ?? []);
  if (lines.length === 1) {
    const total = breakdownItems.reduce((sum, item) => sum + item.amountCents, 0);
    if (total === lines[0].patient_due_cents) {
      labelByLineId.set(
        lines[0].charge_line_id,
        breakdownItems.map((item) => item.label).join(' + '),
      );
    }
    return labelByLineId;
  }

  const remainingItems = [...breakdownItems];
  for (const line of lines) {
    const matchIndex = remainingItems.findIndex(
      (item) => item.amountCents === line.patient_due_cents,
    );
    if (matchIndex === -1) continue;
    const [match] = remainingItems.splice(matchIndex, 1);
    labelByLineId.set(line.charge_line_id, match.label);
  }
  return labelByLineId;
}

function responsibilityBreakdownItems(
  breakdown: PatientResponsibilityBreakdown | null | undefined,
): Array<{ label: string; amountCents: number }> {
  if (!breakdown) return [];
  return [
    { label: 'Copay', amountCents: breakdown.copay_cents ?? 0 },
    { label: 'Coinsurance', amountCents: breakdown.coinsurance_cents ?? 0 },
    { label: 'Deductible', amountCents: breakdown.deductible_cents ?? 0 },
    { label: 'Other responsibility', amountCents: breakdown.other_cents ?? 0 },
  ].filter((item) => item.amountCents > 0);
}
